import React, { createContext, useContext, useEffect, useState } from 'react';
import io from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext(null);

export const useSocket = () => {
    return useContext(SocketContext);
};

// 🔍 Helper: Check if backend server is reachable
const checkServerReachable = async (apiUrl) => {
    if (!apiUrl) return false;
    
    try {
        // Quick health check - 2 second timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 2000);
        
        const response = await fetch(apiUrl + '/health', {
            method: 'GET',
            signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        return response.ok;
    } catch (error) {
        // Server unreachable - this is normal in development when backend is down
        if (process.env.NODE_ENV === 'development') {
            console.log('ℹ️ Backend server not reachable (development mode)');
        }
        return false;
    }
};

export const SocketProvider = ({ children }) => {
    const [socket, setSocket] = useState(null);
    const { user, token } = useAuth();
    const isDevelopment = process.env.NODE_ENV === 'development';

    useEffect(() => {
        // 🔴 CRITICAL: Only attempt socket connection if:
        // 1. User is authenticated (has valid token)
        // 2. API URL is configured
        // 3. We're NOT already connected with the same token
        
        const apiUrl = process.env.REACT_APP_API_URL;
        
        if (!token || !apiUrl) {
            // No token or no API URL - don't attempt connection
            if (socket) {
                console.log('🔌 Disconnecting socket - no auth token');
                socket.disconnect();
                setSocket(null);
            }
            return;
        }

        // Check if we already have a socket for this specific token
        if (socket && socket.auth?.token === token) {
            return;
        }

        // 🔍 Server reachability check - async
        let isMounted = true;

        const initializeSocket = async () => {
            // In development, verify server is actually running
            if (isDevelopment) {
                const serverReachable = await checkServerReachable(apiUrl);
                if (!serverReachable) {
                    if (isMounted) {
                        console.warn('⚠️ Backend server not available - socket connection skipped');
                        setSocket(null);
                    }
                    return;
                }
            }

            // Server is reachable, proceed with socket connection
            if (!isMounted) return;

            console.log(`🔌 [${isDevelopment ? 'DEV' : 'PROD'}] Initializing socket connection...`);

            try {
                const newSocket = io(apiUrl, {
                    auth: { token },
                    reconnection: true,
                    // 🔴 CRITICAL: Limit reconnection attempts
                    // Development: max 3 attempts, stop quickly
                    // Production: max 5 attempts, standard delay
                    reconnectionAttempts: isDevelopment ? 2 : 5,
                    reconnectionDelay: 1000,
                    reconnectionDelayMax: 5000,
                    withCredentials: true,
                    transports: ['websocket', 'polling'],
                    // Timeout before giving up on connection
                    timeout: isDevelopment ? 3000 : 5000,
                });

                newSocket.on('connect', () => {
                    if (isMounted) {
                        console.log('✅ Socket connected:', newSocket.id);
                        setSocket(newSocket);
                    }
                });

                newSocket.on('connect_error', (err) => {
                    console.warn('⚠️ Socket connection error:', err.message);
                    // Non-blocking error - app continues without socket
                });

                newSocket.on('disconnect', (reason) => {
                    console.log('🔌 Socket disconnected:', reason);
                });

                // 🔴 CRITICAL: Proper cleanup
                return () => {
                    if (isMounted) {
                        console.log('🔌 Cleanup: Disconnecting socket...');
                        newSocket.disconnect();
                    }
                };
            } catch (error) {
                console.warn('⚠️ Failed to initialize socket:', error.message);
                if (isMounted) {
                    setSocket(null);
                }
            }
        };

        // Initialize socket asynchronously
        const cleanup = initializeSocket();

        return () => {
            isMounted = false;
            if (cleanup && typeof cleanup === 'function') {
                cleanup();
            }
        };
    }, [token, isDevelopment]);

    return (
        <SocketContext.Provider value={socket}>
            {children}
        </SocketContext.Provider>
    );
};
